import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';
import { useSettings } from '../../../stores/settingsStore';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import S3VideoPlayer from '../../../components/common/S3VideoPlayer';

function timeDiff(start, end, asSeconds = false) {
  if (!start || !end) {
    return 'N/A';
  }
  const startTime = dayjs(start);
  const endTime = dayjs(end);
  return endTime.diff(startTime, asSeconds ? 'seconds' : 'minute');
}

function filenameFromPath(path) {
  const parts = path.split('/');
  return parts[parts.length - 1];
}

function RecordingDetails() {
  const { recordingId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const queryClient = useQueryClient();
  const { pollingInterval } = useSettings();

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [deleteVideo, setDeleteVideo] = useState({ file: '', type: '', open: false });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['recording', recordingId],
    queryFn: () => api.get(`/api/recordings/${recordingId}`),
    refetchInterval: pollingInterval || 5000,
  });

  const recording = data?.recording;

  // Compute derived values
  const botStartupTime = recording ? timeDiff(recording.timeStarted, recording.botJoinedTime, true) : 'N/A';
  const recordingLength = recording ? timeDiff(recording.botJoinedTime, recording.timeEnded) : 'N/A';

  const handleComposeButtonClick = async () => {
    if (!recording) return;
    try {
      await api.post('/api/recording/re-compose', {
        recordingId: recording.recordingId,
        roomName: recording.roomName,
      });
      refetch();
    } catch (err) {
      console.error('Error requesting composition:', err);
    }
  };

  /**
   * The composition's real S3 key, as recorded when it was written.
   *
   * This used to be rebuilt on the client from the signed-in admin's own
   * `metadata.apiKey`, but the composer stores the object under the key that
   * OWNED THE RECORDING. An admin holding more than one API key (recordings
   * are listed across all of them) therefore got a path for the wrong key —
   * playback missed, and the delete button pointed somewhere else entirely.
   * The server already returns the stored key; use it.
   */
  const compositionKey = recording?.composition?.compositionUrl || '';

  const handleVideoClick = (video) => {
    if (!video) return;
    setSelectedVideo(video);
  };

  const handleDeleteClick = async ({ file, type }) => {
    if (!file || !recording) return;
    try {
      await api.delete(`/api/recordings/${recording.recordingId}/file`, {
        file,
        type,
      });
      setDeleteVideo({ file: '', type: '', open: false });
      refetch();
    } catch (err) {
      console.error('Error deleting file:', err);
    }
  };

  const redirectToRoom = (roomId) => {
    navigate(`/rooms/${roomId}`);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error.message}</Typography>;
  }

  if (!recording) {
    return (
      <Box>
        <Typography variant="h5">Recording not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/recordings')}>
          Back to Recordings
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 2, padding: 3 }}>
      <Button onClick={() => navigate('/recordings')} sx={{ mb: 2 }}>
        Back to Recordings
      </Button>
      <Typography variant="h4" gutterBottom>
        Recording Details: {recording.roomName}
      </Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => redirectToRoom(recording.roomId)}
          >
            Go to Room
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => refetch()}
          >
            <RefreshIcon />
          </Button>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
            {/* Recording Panel */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">Recording Details</Typography>
                  <Button
                    disabled={
                      !['completed', 'files uploaded'].includes(recording.status) ||
                      recording?.files?.some(file => file.endsWith('removed'))
                    }
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleComposeButtonClick}
                  >
                    Re-Compose
                  </Button>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '5px' }}>
                  <Typography><strong>Recording ID:</strong></Typography>
                  <Typography>{recording.recordingId}</Typography>
                  <Typography><strong>Request Time:</strong></Typography>
                  <Typography>{dayjs(recording.timeStarted).format('YYYY-MM-DD HH:mm:ss')}</Typography>
                  <Typography><strong>Recorder Started:</strong></Typography>
                  <Typography>{recording.botJoinedTime ? dayjs(recording.botJoinedTime).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}</Typography>
                  <Typography><strong>Startup Time:</strong></Typography>
                  <Typography>{botStartupTime} secs</Typography>
                  <Typography><strong>Time Ended:</strong></Typography>
                  <Typography>{recording.timeEnded ? dayjs(recording.timeEnded).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}</Typography>
                  <Typography><strong>Recording Length:</strong></Typography>
                  <Typography>{recordingLength} mins</Typography>
                  <Typography><strong>Type:</strong></Typography>
                  <Typography>{recording.recordingType}</Typography>
                  <Typography><strong>Status:</strong></Typography>
                  <Typography>{recording.status}</Typography>
                  <Typography><strong>Server IP:</strong></Typography>
                  <Typography>{recording.botIP || 'N/A'}</Typography>
                </Box>

                <Typography sx={{ mt: 2 }}><strong>Recorded Files:</strong></Typography>
                {recording?.files?.map((file, fileIndex) => (
                  <ListItemButton
                    sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
                    key={fileIndex}
                  >
                    <ListItemText primary={filenameFromPath(file)} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        disabled={file?.endsWith('removed')}
                        edge="end"
                        aria-label="play"
                        onClick={(e) => { e.stopPropagation(); handleVideoClick(file); }}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                      <IconButton
                        disabled={file?.endsWith('removed')}
                        edge="end"
                        aria-label="delete"
                        onClick={(e) => { e.stopPropagation(); setDeleteVideo({ file, type: 'recording', open: true }); }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItemButton>
                ))}
              </Paper>
            </Grid>

            {/* Composition Panel */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" gutterBottom>Composition Details</Typography>
                {recording?.composition?.compositionStatus ? (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '5px' }}>
                      <Typography><strong>Status:</strong></Typography>
                      <Typography>{recording.composition?.compositionStatus}</Typography>
                      <Typography><strong>Time Started:</strong></Typography>
                      <Typography>
                        {recording.composition?.timeStarted
                          ? dayjs(recording.composition?.timeStarted).format('YYYY-MM-DD HH:mm:ss')
                          : 'N/A'}
                      </Typography>
                      <Typography><strong>Time Ended:</strong></Typography>
                      <Typography>
                        {recording.composition?.timeEnded
                          ? dayjs(recording.composition?.timeEnded).format('YYYY-MM-DD HH:mm:ss')
                          : 'N/A'}
                      </Typography>
                      <Typography><strong>Encoding Time:</strong></Typography>
                      <Typography>
                        {timeDiff(recording.composition?.timeStarted, recording.composition?.timeEnded)} mins
                      </Typography>
                    </Box>
                    <Typography sx={{ mt: 2 }}><strong>Composition File:</strong></Typography>
                    {recording?.composition?.compositionStatus === 'completed' ||
                    recording?.composition?.compositionStatus === 'removed' ? (
                      <ListItemButton sx={{ cursor: 'pointer' }}>
                        <ListItemText
                          primary={`${recording.recordingId}_grid.mp4${
                            recording.composition?.compositionStatus === 'removed' ? '-removed' : ''
                          }`}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            disabled={!compositionKey || recording.composition?.compositionStatus === 'removed'}
                            edge="end"
                            aria-label="play"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVideoClick(compositionKey);
                            }}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                          <IconButton
                            disabled={!compositionKey || recording.composition?.compositionStatus === 'removed'}
                            edge="end"
                            aria-label="delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteVideo({
                                file: compositionKey,
                                type: 'composition',
                                open: true,
                              });
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </ListItemButton>
                    ) : (
                      <Typography>No composition file available</Typography>
                    )}
                  </>
                ) : (
                  <Typography>No composition details available</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {selectedVideo && (
          <S3VideoPlayer
            videoUrl={selectedVideo}
            onClose={() => setSelectedVideo(null)}
            title={filenameFromPath(selectedVideo)}
          />
        )}
      </Paper>
      <ConfirmDialog
        open={deleteVideo.open}
        title="Delete Video"
        caption="Are you sure you want to delete this video? Please be aware that once deleted it cannot be retrieved."
        onOk={() => handleDeleteClick(deleteVideo)}
        onCancel={() => setDeleteVideo({ file: '', type: '', open: false })}
      />
    </Box>
  );
}

export default RecordingDetails;
